<script setup>
import { computed, ref } from 'vue';
import { useI18n } from 'vue-i18n';
import { getOutPorts } from '../../composables/useCsmlPorts';
import { NODE_WIDTH } from '../../constants/csmlLayout';
import {
  CATEGORY_STYLES,
  NODE_CATEGORY_IDS,
  getNodeCategory,
} from '../../constants/csmlNodeCategories';

const props = defineProps({
  nodes: { type: Array, default: () => [] },
  connections: { type: Array, default: () => [] },
  selectedConnection: { type: Object, default: null },
  tempConnection: { type: Object, default: null },
  portOffsets: { type: Object, default: () => ({}) },
  offsetX: { type: Number, default: 0 },
  offsetY: { type: Number, default: 0 },
  zoom: { type: Number, default: 1 },
  wireStyle: {
    type: String,
    default: 'bezier',
    validator: value => ['bezier', 'step'].includes(value),
  },
});

const emit = defineEmits(['selectConnection', 'removeConnection']);

const { t } = useI18n();

// One arrow marker per stroke colour: SVG markers do not inherit the path's
// stroke in every browser we support, so each colour needs its own.
const markers = [
  ...NODE_CATEGORY_IDS.map(id => ({
    id: `csml-arrow-${id}`,
    fill: CATEGORY_STYLES[id].stroke,
  })),
  { id: 'csml-arrow-selected', fill: null },
];

// Used only for the brief window before a node's ports have reported their
// real measured offsets (see CsmlFlowNode's ResizeObserver) - e.g. the very
// first paint right after a node is added.
const FALLBACK_WIDTH = NODE_WIDTH;
const FALLBACK_HEIGHT = 80;

const hoveredKey = ref(null);

const connectionKey = conn =>
  `${conn.fromId}-${conn.toId}-${conn.fromPortIdx ?? 0}`;

function portPosition(node, side, portIdx) {
  if (!node) return { x: 0, y: 0 };
  const measured = props.portOffsets[node.id]?.[side];
  const port = measured?.find(p => p.idx === portIdx) || measured?.[0];
  if (port) {
    return { x: node.x + port.x, y: node.y + port.y };
  }
  return side === 'in'
    ? { x: node.x, y: node.y + FALLBACK_HEIGHT / 2 }
    : { x: node.x + FALLBACK_WIDTH, y: node.y + FALLBACK_HEIGHT / 2 };
}

function controlOffset(x1, x2) {
  return Math.max(40, Math.abs(x2 - x1) * 0.4);
}

function bezierCurve(x1, y1, x2, y2) {
  const dx = controlOffset(x1, x2);
  return `M ${x1} ${y1} C ${x1 + dx} ${y1}, ${x2 - dx} ${y2}, ${x2} ${y2}`;
}

// Midpoint of the cubic at t=0.5 reduces to (P0 + 3P1 + 3P2 + P3) / 8, which
// is where the inline delete control sits so it always lands on the wire.
function bezierCurveMidpoint(x1, y1, x2, y2) {
  const dx = controlOffset(x1, x2);
  return {
    x: (x1 + 3 * (x1 + dx) + 3 * (x2 - dx) + x2) / 8,
    y: (y1 + 3 * y1 + 3 * y2 + y2) / 8,
  };
}

// Orthogonal routing with rounded corners. A target to the right gets one
// vertical jog halfway across; a target at or behind the source leaves
// to the right, drops to the midline and approaches from the left instead.
const STEP_RADIUS = 12;
const STEP_PAD = 24;

function stepRoute(x1, y1, x2, y2) {
  if (x2 - x1 > STEP_RADIUS * 2 + 8) {
    const mx = x1 + (x2 - x1) / 2;
    const dy = y2 - y1;
    if (Math.abs(dy) < 1)
      return { d: `M ${x1} ${y1} L ${x2} ${y2}`, mid: { x: mx, y: y1 } };
    const r = Math.min(STEP_RADIUS, Math.abs(dy) / 2);
    const s = dy > 0 ? 1 : -1;
    return {
      d: `M ${x1} ${y1} H ${mx - r} Q ${mx} ${y1} ${mx} ${y1 + s * r} V ${y2 - s * r} Q ${mx} ${y2} ${mx + r} ${y2} H ${x2}`,
      mid: { x: mx, y: (y1 + y2) / 2 },
    };
  }
  const midY = Math.abs(y2 - y1) < 1 ? y1 + STEP_PAD * 2 : (y1 + y2) / 2;
  return {
    d: `M ${x1} ${y1} H ${x1 + STEP_PAD} V ${midY} H ${x2 - STEP_PAD} V ${y2} H ${x2}`,
    mid: { x: (x1 + x2) / 2, y: midY },
  };
}

const bezierPath = (x1, y1, x2, y2) =>
  props.wireStyle === 'step'
    ? stepRoute(x1, y1, x2, y2).d
    : bezierCurve(x1, y1, x2, y2);

const bezierMidpoint = (x1, y1, x2, y2) =>
  props.wireStyle === 'step'
    ? stepRoute(x1, y1, x2, y2).mid
    : bezierCurveMidpoint(x1, y1, x2, y2);

// Wires that leave a branching node carry the branch's name so a fork can be
// read straight off the canvas, without hovering each wire.
const LABEL_MAX_CHARS = 14;
const LABEL_CHAR_WIDTH = 5.6;

function branchLabel(from, conn) {
  const ports = getOutPorts(from);
  if (ports.length < 2) return null;
  const port = ports.find(p => p.idx === (conn.fromPortIdx ?? 0));
  if (!port) return null;
  const text = port.labelKey
    ? t(`AGENT_BOTS.VISUAL_BUILDER.PORTS.${port.labelKey}`)
    : port.label;
  const shown =
    text.length > LABEL_MAX_CHARS
      ? `${text.slice(0, LABEL_MAX_CHARS - 1)}…`
      : text;
  return { text: shown, width: shown.length * LABEL_CHAR_WIDTH + 14 };
}

const worldTransform = computed(
  () => `translate(${props.offsetX} ${props.offsetY}) scale(${props.zoom})`
);

const paths = computed(() => {
  return props.connections
    .map(conn => {
      const from = props.nodes.find(n => n.id === conn.fromId);
      const to = props.nodes.find(n => n.id === conn.toId);
      if (!from || !to) return null;
      const start = portPosition(from, 'out', conn.fromPortIdx ?? 0);
      const end = portPosition(to, 'in', 0);
      const selected =
        props.selectedConnection &&
        props.selectedConnection.fromId === conn.fromId &&
        props.selectedConnection.toId === conn.toId &&
        props.selectedConnection.fromPortIdx === conn.fromPortIdx;
      const category = getNodeCategory(from.type);
      return {
        conn,
        key: connectionKey(conn),
        d: bezierPath(start.x, start.y, end.x, end.y),
        mid: bezierMidpoint(start.x, start.y, end.x, end.y),
        label: branchLabel(from, conn),
        start,
        stroke: CATEGORY_STYLES[category].stroke,
        marker: selected ? 'csml-arrow-selected' : `csml-arrow-${category}`,
        selected,
      };
    })
    .filter(Boolean);
});

const tempPath = computed(() => {
  const temp = props.tempConnection;
  if (!temp) return null;
  const from = props.nodes.find(n => n.id === temp.fromId);
  if (!from) return null;
  const start = portPosition(from, 'out', temp.fromPortIdx ?? 0);
  return bezierPath(start.x, start.y, temp.x, temp.y);
});

const isActive = item => item.selected || hoveredKey.value === item.key;
</script>

<template>
  <!-- Fills the canvas viewport (real width/height) and mirrors the flow
       world's pan/zoom on an inner group. Sitting inside the unsized world
       div with percentage sizing collapsed the SVG to 0×0 so wires vanished. -->
  <svg
    class="absolute inset-0 z-[1] w-full h-full pointer-events-none overflow-visible"
  >
    <defs>
      <marker
        v-for="marker in markers"
        :id="marker.id"
        :key="marker.id"
        viewBox="0 0 10 10"
        refX="9"
        refY="5"
        markerWidth="5"
        markerHeight="5"
        orient="auto-start-reverse"
      >
        <path
          d="M 0 0 L 10 5 L 0 10 z"
          :style="marker.fill ? { fill: marker.fill } : undefined"
          :class="{ 'fill-n-brand': !marker.fill }"
        />
      </marker>
    </defs>
    <g :transform="worldTransform">
      <g v-for="item in paths" :key="item.key">
        <!-- A wide transparent path under the visible one: a 2px stroke is far
             too small a target to click or hover reliably. -->
        <path
          :d="item.d"
          fill="none"
          stroke="transparent"
          stroke-width="14"
          class="cursor-pointer [pointer-events:stroke]"
          @click.stop="emit('selectConnection', item.conn)"
          @mouseenter="hoveredKey = item.key"
          @mouseleave="hoveredKey = null"
        />
        <path
          :d="item.d"
          fill="none"
          :style="item.selected ? undefined : { stroke: item.stroke }"
          :stroke-width="isActive(item) ? 2.5 : 1.75"
          :marker-end="`url(#${item.marker})`"
          stroke-linejoin="round"
          class="pointer-events-none transition-[stroke-width] duration-150"
          :class="{ 'stroke-n-brand': item.selected }"
        />
        <g v-if="item.label && !isActive(item)" class="pointer-events-none">
          <rect
            :x="item.start.x + 14"
            :y="item.start.y - 8"
            :width="item.label.width"
            height="16"
            rx="8"
            fill="rgb(var(--slate-2))"
            :style="{ stroke: item.stroke }"
            stroke-width="1"
          />
          <text
            :x="item.start.x + 14 + item.label.width / 2"
            :y="item.start.y + 3"
            text-anchor="middle"
            font-size="9"
            font-weight="500"
            fill="rgb(var(--slate-12))"
          >
            {{ item.label.text }}
          </text>
        </g>
        <g
          v-if="isActive(item)"
          class="cursor-pointer [pointer-events:all]"
          @click.stop="emit('removeConnection', item.conn)"
          @mouseenter="hoveredKey = item.key"
          @mouseleave="hoveredKey = null"
        >
          <circle
            :cx="item.mid.x"
            :cy="item.mid.y"
            r="8"
            fill="rgb(var(--slate-1))"
            :style="item.selected ? undefined : { stroke: item.stroke }"
            stroke-width="1.5"
            :class="{ 'stroke-n-brand': item.selected }"
          />
          <path
            :d="`M ${item.mid.x - 3} ${item.mid.y - 3} L ${item.mid.x + 3} ${
              item.mid.y + 3
            } M ${item.mid.x + 3} ${item.mid.y - 3} L ${item.mid.x - 3} ${
              item.mid.y + 3
            }`"
            :style="item.selected ? undefined : { stroke: item.stroke }"
            stroke-width="1.5"
            stroke-linecap="round"
            :class="{ 'stroke-n-brand': item.selected }"
          />
        </g>
      </g>
      <path
        v-if="tempPath"
        :d="tempPath"
        fill="none"
        class="stroke-n-brand animate-csml-wire-dash"
        stroke-width="2"
        stroke-dasharray="6 4"
        stroke-linecap="round"
        marker-end="url(#csml-arrow-selected)"
      />
    </g>
  </svg>
</template>
